import React, {ReactElement, useMemo} from 'react';
import {Typography} from '@customink/pigment-react';
import Skeleton from '@mui/material/Skeleton';
import {useGetStoreDetailsQuery} from '../../api/storesApi';
import {useGroupOrderTimeline, useAppSelector} from '../../hooks';
import {selectPrimaryCampaign} from '../../slices/storesSlice';
import {useStoreType} from '../../hooks/useStoreType';
import ProfitProgressBar from '../OrderStatusBar/ProfitProgressBar';
import GroupProgressBar from '../OrderStatusBar/GroupProgressBar';
import {campaignDaysLeft} from '../../utils/helpers';
import {StoreDetails} from '../../models/storeDetails';
import './index.scss';

type Props = {
  storeSlug: string;
};

type CloseDateTextProps = {
  storeDetails: StoreDetails;
  daysUntilClose: string;
  closeDateFormatted: string;
};

const CloseDateText: React.FC<CloseDateTextProps> = ({
  storeDetails,
  daysUntilClose,
  closeDateFormatted
}: CloseDateTextProps): ReactElement => (
  <div className="tracker-status">
    <div className="closeDate">
      {/* PENDING: Remove fontSize prop override with Pigment update */}
      <Typography variant="captionSmall" fontSize={13}>
        <div className="closeDate-title">Order Close Date:</div>
        {closeDateFormatted}
      </Typography>
      {storeDetails?.status !== 'closed' ? (
        <Typography
          aria-labelledby="days"
          className="daysLeft"
          variant="captionSmall"
          fontSize={13}>
          Open for {campaignDaysLeft(daysUntilClose)}
        </Typography>
      ) : null}
    </div>
  </div>
);

const TrackerPlaceHolder: React.FC = (): ReactElement => {
  return (
    <div className="tracker-placeHolder">
      <Skeleton variant="text" className="placeHolder-order" />
      <Skeleton animation="wave" className="placeHolder-progressBar" />
      <Skeleton variant="text" className="placeHolder-amount" />
    </div>
  );
};

const Tracker: React.FC<Props> = ({storeSlug}: Props): ReactElement => {
  const {isLoading} = useGetStoreDetailsQuery(storeSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const {isStoreTypeGroupOrder} = useStoreType();
  const {closeDateFormatted, daysUntilClose} = useGroupOrderTimeline();

  const renderOrderStatus = useMemo(() => {
    // PENDING: [Phelan Lucky] temporarily hard-coded, remove once fundraiser is over
    return storeSlug === 'phelan-lucky' ? (
      <ProfitProgressBar
        storeSlug={storeSlug}
        primaryCampaign={primaryCampaign}>
        <CloseDateText
          storeDetails={storeDetails}
          daysUntilClose={daysUntilClose}
          closeDateFormatted={closeDateFormatted}
        />
      </ProfitProgressBar>
    ) : (
      <GroupProgressBar primaryCampaign={primaryCampaign}>
        <CloseDateText
          storeDetails={storeDetails}
          daysUntilClose={daysUntilClose}
          closeDateFormatted={closeDateFormatted}
        />
      </GroupProgressBar>
    );
  }, [daysUntilClose, closeDateFormatted]);

  return isStoreTypeGroupOrder ? (
    <div className="storeProductDetailSidebar-tracker-wrapper">
      <div className="tracker">
        {isLoading ? <TrackerPlaceHolder /> : renderOrderStatus}
      </div>
    </div>
  ) : null;
};

export default Tracker;
